import { useEffect, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { Field, SectionCard } from "./shared";

export default function BankReconciliationPage() {
  const [bankRows, setBankRows] = useState([]);
  const [bankCsvText, setBankCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/society/bank-statements").catch(() => ({ data: [] }));
      setBankRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load bank statement rows"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const readCsvFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBankCsvText(String(reader.result || ""));
    reader.onerror = () => toast.error("Could not read CSV file");
    reader.readAsText(file);
  };

  const importStatement = async () => {
    try {
      setBusy(true);
      const res = await api.post("/society/bank-statements/import", { csvText: bankCsvText, account: "Main bank" });
      toast.success(`Imported ${res.data?.imported || 0}. Matched ${res.data?.matched || 0}, unmatched ${res.data?.unmatched || 0}`);
      setBankCsvText("");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not import bank statement"));
    } finally {
      setBusy(false);
    }
  };

  const recheck = async () => {
    try {
      setBusy(true);
      const res = await api.post("/society/bank-statements/reconcile");
      toast.success(`Checked ${res.data?.checked || 0}. Matched ${res.data?.matched || 0}, unmatched ${res.data?.unmatched || 0}`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not reconcile bank statement"));
    } finally {
      setBusy(false);
    }
  };

  const exportReport = async () => {
    try {
      const res = await api.get("/society/audit/export", { params: { report: "bank_reconciliation" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "society-bank_reconciliation.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err, "Could not export report"));
    }
  };

  const matched = bankRows.filter((r) => r.reconcileStatus === "matched").length;
  const unmatched = bankRows.filter((r) => r.reconcileStatus === "unmatched").length;
  const duplicate = bankRows.filter((r) => r.reconcileStatus === "duplicate").length;

  return (
    <div className="space-y-5">
      <SectionCard
        title="Bank reconciliation"
        subtitle={`Matched ${matched} | Unmatched ${unmatched} | Duplicate ${duplicate}`}
        actions={(
          <>
            <Button variant="outline" size="sm" disabled={busy} onClick={recheck}>Recheck</Button>
            <Button variant="outline" size="sm" onClick={exportReport}><Download className="mr-2 h-4 w-4" /> Export</Button>
          </>
        )}
      >
        <div className="grid gap-3 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4">
          <Field label="Upload bank CSV">
            <Input type="file" accept=".csv,text/csv" onChange={(e) => readCsvFile(e.target.files?.[0])} />
          </Field>
          <Field label="CSV preview / paste">
            <Textarea rows={5} value={bankCsvText} onChange={(e) => setBankCsvText(e.target.value)} placeholder="Date,Narration,Debit,Credit,Balance,UTR" />
          </Field>
          <Button disabled={busy || !bankCsvText.trim()} onClick={importStatement} className="w-fit bg-[#111827] text-white hover:bg-[#374151]">
            <Upload className="mr-2 h-4 w-4" /> Import and match
          </Button>
        </div>

        <div className="mt-4 overflow-x-auto rounded-sm border border-[#E5E7EB]">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Date</TableHead><TableHead>Narration</TableHead><TableHead>Reference</TableHead><TableHead>Credit</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
              ) : bankRows.slice(0, 30).map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.transactionDate}</TableCell>
                  <TableCell className="max-w-[280px] truncate">{row.narration || "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{row.referenceNo || "-"}</TableCell>
                  <TableCell>₹ {Number(row.credit || 0).toLocaleString()}</TableCell>
                  <TableCell>{row.reconcileStatus}</TableCell>
                </TableRow>
              ))}
              {!loading && bankRows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-[#6B7280]">No bank statement rows imported.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </SectionCard>
    </div>
  );
}
